export const FULLSCREEN_NATIVE_DELAY_MS = 0;

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

export function getActiveFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

export async function exitNativeFullscreen(): Promise<void> {
  const doc = document as FullscreenDocument;
  if (!getActiveFullscreenElement()) return;

  try {
    if (doc.exitFullscreen) {
      await doc.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      await doc.webkitExitFullscreen();
    }
  } catch (error) {
    console.warn("[fullscreen] native exit failed", error);
  }
}

export async function enterNativeFullscreen(
  container: HTMLElement,
): Promise<boolean> {
  const el = container as FullscreenElement;

  try {
    const active = getActiveFullscreenElement();
    if (active && active !== container) {
      await exitNativeFullscreen();
    }
    if (isElementFullscreen(container)) return true;

    if (el.requestFullscreen) {
      await el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      await el.webkitRequestFullscreen();
    }
    return isElementFullscreen(container);
  } catch (error) {
    console.warn("[fullscreen] native enter failed", error);
    return false;
  }
}

function isElementFullscreen(container: Element | null): boolean {
  const active = getActiveFullscreenElement();
  return !!container && active === container;
}

export const FULLSCREEN_CHANGE_EVENTS = [
  "fullscreenchange",
  "webkitfullscreenchange",
] as const;

export interface FullscreenControllerOptions {
  getTarget: () => HTMLElement | null | undefined;
  onChange?: (open: boolean) => void;
  onResize?: () => void;
  fallbackClass?: string;
  shouldUseFallback?: () => boolean;
}

/**
 * Bir fullscreen hedefinin durumunu yönetir. Her tüketici kendi instance'ını
 * oluşturduğu için UI durumu ve bekleyen giriş işlemi ürünler arasında paylaşılmaz.
 */
export class FullscreenController {
  private open = false;
  private connected = false;
  private enterTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: FullscreenControllerOptions) {}

  get isOpen(): boolean {
    return this.open;
  }

  connect(): void {
    if (this.connected) return;
    this.connected = true;
    for (const event of FULLSCREEN_CHANGE_EVENTS) {
      document.addEventListener(event, this.onFullscreenChange);
    }
    this.syncState();
  }

  dispose(): void {
    if (this.connected) {
      for (const event of FULLSCREEN_CHANGE_EVENTS) {
        document.removeEventListener(event, this.onFullscreenChange);
      }
      this.connected = false;
    }
    this.clearEnterTimer();
    this.setFallback(false);
    this.setOpen(false);
  }

  async toggle(): Promise<boolean> {
    return this.open ? this.exit() : this.enter();
  }

  async enter(): Promise<boolean> {
    const target = this.options.getTarget();
    if (!target) return false;

    this.setOpen(true);
    this.options.onResize?.();
    this.scheduleEnter(target);
    return true;
  }

  async exit(): Promise<boolean> {
    this.clearEnterTimer();
    this.setFallback(false);
    this.setOpen(false);

    if (isElementFullscreen(this.options.getTarget() ?? null)) {
      await exitNativeFullscreen();
    }
    this.options.onResize?.();
    return false;
  }

  private scheduleEnter(target: HTMLElement): void {
    this.clearEnterTimer();
    const run = async () => {
      this.enterTimer = null;
      if (!this.open || this.options.getTarget() !== target) return;

      const entered = await enterNativeFullscreen(target);
      this.setFallback(
        !entered && (this.options.shouldUseFallback?.() ?? false),
        target,
      );
      if (!entered && !this.hasFallback(target)) {
        this.setOpen(false);
      }
      this.options.onResize?.();
    };

    if (FULLSCREEN_NATIVE_DELAY_MS <= 0) {
      void run();
    } else {
      this.enterTimer = setTimeout(
        () => void run(),
        FULLSCREEN_NATIVE_DELAY_MS,
      );
    }
  }

  private readonly onFullscreenChange = () => {
    requestAnimationFrame(() => this.syncState());
  };

  private syncState(): void {
    const target = this.options.getTarget();
    const nativeOpen = isElementFullscreen(target ?? null);
    if (!nativeOpen && this.enterTimer === null) {
      this.setFallback(false, target ?? undefined);
    }
    this.setOpen(nativeOpen || this.hasFallback(target ?? undefined));
    this.options.onResize?.();
  }

  private setOpen(open: boolean): void {
    if (this.open === open) return;
    this.open = open;
    this.options.onChange?.(open);
  }

  private setFallback(open: boolean, target = this.options.getTarget()): void {
    if (!target || !this.options.fallbackClass) return;
    target.classList.toggle(this.options.fallbackClass, open);
  }

  private hasFallback(target = this.options.getTarget()): boolean {
    return !!(
      target &&
      this.options.fallbackClass &&
      target.classList.contains(this.options.fallbackClass)
    );
  }

  private clearEnterTimer(): void {
    if (this.enterTimer === null) return;
    clearTimeout(this.enterTimer);
    this.enterTimer = null;
  }
}
